<template>
  <div id="toast-wrapper" class="fixed z-50">
    <TheToast to="#toast-wrapper" />
  </div>

  <ClientOnly>
    <ErrorBoundary :owners="['bot-squad-lifecycle-front']">
      <LeadCollectionPopup v-if="canAttachLeadCollection" />
    </ErrorBoundary>
  </ClientOnly>

  <!-- The whole page (sticky header included) is pushed left to make room for
       the assistant side panel when it squeezes the layout (open + ≥md). The
       margin width mirrors the panel width via the shared `panelWidthPx` so they
       can't drift (narrower between md and lg); the transition is timed to match
       the panel's slide-in. Below md the panel is full-screen, so no margin. -->
  <div
    class="transition-[margin-right] duration-[250ms] ease-out"
    :style="{
      marginRight: isSqueezing ? `${panelWidthPx}px` : '0px',
    }"
  >
    <slot name="header">
      <ErrorBoundary :owners="['bot-squad-search-and-recommendation-front']">
        <Transition enter-from-class="opacity-0" leave-to-class="opacity-0">
          <RevBackdrop
            v-if="isMenuActive || isSearchBarActive"
            class="z-10 hidden backdrop-blur-2xl transition-opacity duration-200 md:block"
          />
        </Transition>

        <TheHeader />

        <ClientOnly>
          <AddToCartPopover />
        </ClientOnly>
      </ErrorBoundary>
    </slot>

    <slot />

    <slot name="footer">
      <ErrorBoundary :owners="['bot-squad-search-and-recommendation-front']">
        <Footer />
      </ErrorBoundary>
    </slot>
  </div>

  <!-- Rendered last so the side panel (z-10) paints above the page header
       (TheHeader, also z-10) at equal z-index, while staying below the DS modals
       and toasts, which are teleported to <body> / sit at z-50. -->
  <ClientOnly>
    <ErrorBoundary :owners="['bot-group-ai-shopping-assistant-front']">
      <WebShoppingAssistantHost v-if="isShoppingAssistantWebEnabled" />
      <WebShoppingAssistantFAB v-if="isShoppingAssistantWebEnabled" />
    </ErrorBoundary>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useHead } from '#imports'

import AddToCartPopover from '@backmarket/nuxt-layer-recommendation/AddToCartPopover.vue'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'
import TheToast from '@backmarket/nuxt-module-toast/TheToast.vue'
import { RevBackdrop } from '@ds/components/Backdrop'

import Footer from '~/scopes/footer/components/AppFooter.vue'
import TheHeader from '~/scopes/navigation/components/TheHeader/TheHeader.vue'
import { useActiveMenu } from '~/scopes/navigation/composables/useActiveMenu'
import { useToggleSearchBar } from '~/scopes/search-bar/composables/useToggleSearchBar'
import WebShoppingAssistantFAB from '~/scopes/shopping-assistant/components/WebShoppingAssistantFAB.vue'
import WebShoppingAssistantHost from '~/scopes/shopping-assistant/components/WebShoppingAssistantHost.vue'
import { useIsShoppingAssistantWebEnabled } from '~/scopes/shopping-assistant/composables/useIsShoppingAssistantWebEnabled'
import { useShoppingAssistantViewport } from '~/scopes/shopping-assistant/composables/useShoppingAssistantViewport'

import LeadCollectionPopup from '../scopes/lead-collection/LeadCollectionPopup.vue'
import { useLeadCollectionPopup } from '../scopes/lead-collection/composables/useLeadCollectionPopup'

const { isSearchBarActive } = useToggleSearchBar()
const { canAttachLeadCollection } = useLeadCollectionPopup()
const { isSqueezing, panelWidthPx } = useShoppingAssistantViewport()

const isShoppingAssistantWebEnabled = useIsShoppingAssistantWebEnabled()

const { isMenuActive } = useActiveMenu()
useHead({
  htmlAttrs: {
    lang: useI18nLocale(),
  },
})
</script>
